// Canvas variables (2026-10-09): values a canvas defines once and its blocks
// use — a DATE (a date range applied across the canvas, which any block may
// override) or a TEXT ({key} in a prompt, a filter value, a Keyword Planner
// setting or a text block). Set in Canvas settings; a block may give a
// variable its own value (`config.variableValues`).
//
//   canvas.variables           [{ key, label, type: "text" | "date", value }]
//   canvas.defaultDateVariable the date variable applied to every dated block
//   block.config.variableValues { [key]: value } — this block's own values
//   block.config.dateOverride   true = this block keeps its own date range
//   block.config.dateRange      "var:<key>" = this block uses that date variable
//
// The stored block config is never rewritten: resolveBlockConfig builds the
// config a RUN reads, and every export renders text through the same
// substitution, so a copy carries the variables and their values as they are.
//
// BYTE-IDENTICAL at server/services/cognitive-flows/canvasVariables.js and
// client/src/cognitive-flows/components/canvas/canvasVariables.js
// (canvasVariables.test.js fails on drift). Pure: no imports.

export const MAX_CANVAS_VARIABLES = 30;
export const MAX_TEXT_VALUE = 2000;
export const CANVAS_VARIABLE_KEY_RE = /^[a-z][a-z0-9_]{0,30}$/;
export const CANVAS_VARIABLE_TYPES = ["text", "date"];
export const DATE_VARIABLE_PREFIX = "var:";

/** The fields one date value carries — the canvas block's own date fields. */
export const DATE_VALUE_FIELDS = ["dateRange", "dateRangeX", "dateRangeMonths", "includeCurrentMonth", "includeToday", "dateFrom", "dateTo"];

/** Block types whose reads take a date range. */
export const DATED_BLOCK_TYPES = new Set(["agent", "chart", "table", "scorecard", "pivot", "workflow"]);

export const DATE_RANGE_LABELS = {
    "": "Source default",
    today: "Today",
    yesterday: "Yesterday",
    last_7: "Last 7 days",
    last_14: "Last 14 days",
    last_30: "Last 30 days",
    last_x: "Last N days",
    last_x_months: "Last N months",
    last_month: "Last month",
    this_month: "This month",
    custom: "Custom",
};

/**
 * "Last N days" ending TODAY: a date-range picker choice, never a stored range.
 * It is stored as `last_x` + `includeToday: true`, which every ads, Google
 * Analytics and Shopify fetcher already reads; plain `last_x` ends yesterday.
 */
export const LAST_X_TODAY_CHOICE = "last_x_today";

/** The date-range picker's choices, in order: every range, plus "Last N days (including today)" after "Last N days". */
export const DATE_RANGE_CHOICES = Object.entries(DATE_RANGE_LABELS).flatMap(([key, label]) => (
    key === "last_x" ? [[key, label], [LAST_X_TODAY_CHOICE, "Last N days (including today)"]] : [[key, label]]
));

/** The picker choice a date value (or a block config) shows as. */
export function dateRangeChoiceOf(value) {
    const v = isObj(value) ? value : {};
    const range = typeof v.dateRange === "string" ? v.dateRange : "";
    return range === "last_x" && v.includeToday === true ? LAST_X_TODAY_CHOICE : range;
}

/** The date fields a picker choice sets: `includeToday` is on only for "Last N days (including today)". */
export function dateFieldsForChoice(choice) {
    if (choice === LAST_X_TODAY_CHOICE) return { dateRange: "last_x", includeToday: true };
    return { dateRange: typeof choice === "string" ? choice : "", includeToday: false };
}

const DATE_RANGE_RE = /^[a-z0-9_]{1,32}$/;
const ISO_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const TEXT_TOKEN_RE = /\{([a-z][a-z0-9_]{0,30})\}/g;
const SCOPE_TOKEN_RE = /\{\{\s*([^}]+?)\s*\}\}/g;

const isObj = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const digits = (v, max) => {
    const n = parseInt(String(v ?? ""), 10);
    return Number.isInteger(n) && n > 0 ? String(Math.min(n, max)) : "";
};

/** A key for a label: lowercase words joined by "_", unique among `taken`. */
export function variableKeyFromLabel(label, taken = []) {
    const used = new Set(taken);
    let base = String(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").replace(/^[^a-z]+/, "");
    if (!base) base = "var";
    base = base.slice(0, 26);
    let key = base;
    for (let i = 2; used.has(key); i += 1) key = `${base}_${i}`;
    return key;
}

/** A date value as stored, or null when it is not one. */
export function sanitizeDateValue(raw) {
    if (!isObj(raw)) return null;
    // The picker choice is never stored as a range: it is last_x ending today.
    if (raw.dateRange === LAST_X_TODAY_CHOICE) return sanitizeDateValue({ ...raw, ...dateFieldsForChoice(LAST_X_TODAY_CHOICE) });
    const range = typeof raw.dateRange === "string" ? raw.dateRange.trim() : "";
    if (range && (!DATE_RANGE_RE.test(range) || range.startsWith("var"))) return null;
    const out = { dateRange: range };
    if (range === "last_x") out.dateRangeX = digits(raw.dateRangeX, 730) || "30";
    if (range === "last_x_months") {
        out.dateRangeMonths = digits(raw.dateRangeMonths, 36) || "3";
        out.includeCurrentMonth = raw.includeCurrentMonth !== false;
    }
    if (range === "custom") {
        out.dateFrom = ISO_DAY_RE.test(String(raw.dateFrom || "")) ? String(raw.dateFrom) : "";
        out.dateTo = ISO_DAY_RE.test(String(raw.dateTo || "")) ? String(raw.dateTo) : "";
    }
    if (raw.includeToday === true) out.includeToday = true;
    return out;
}

function sanitizeValue(type, raw) {
    if (type === "date") return sanitizeDateValue(raw) || { dateRange: "" };
    return typeof raw === "string" ? raw.slice(0, MAX_TEXT_VALUE) : "";
}

/** Stored / submitted canvas variables → the clean list (unique keys, capped). */
export function sanitizeCanvasVariables(raw) {
    const out = [];
    const seen = new Set();
    for (const item of Array.isArray(raw) ? raw : []) {
        if (out.length >= MAX_CANVAS_VARIABLES) break;
        if (!isObj(item)) continue;
        const key = typeof item.key === "string" ? item.key.trim() : "";
        if (!CANVAS_VARIABLE_KEY_RE.test(key) || seen.has(key)) continue;
        const type = CANVAS_VARIABLE_TYPES.includes(item.type) ? item.type : "text";
        seen.add(key);
        out.push({
            key,
            label: String(item.label ?? "").trim().slice(0, 80) || key,
            type,
            value: sanitizeValue(type, item.value),
        });
    }
    return out;
}

/** The canvas-wide date variable, kept only when it names a date variable. */
export function sanitizeDefaultDateVariable(key, variables) {
    const k = typeof key === "string" ? key.trim() : "";
    if (!k) return null;
    return sanitizeCanvasVariables(variables).some((v) => v.key === k && v.type === "date") ? k : null;
}

/**
 * A block's own values. Without `variables` every well-formed key is kept (a
 * block saved before a variable exists keeps its value); with them, a value
 * must match its variable's type.
 */
export function sanitizeVariableValues(raw, variables = null) {
    if (!isObj(raw)) return {};
    const types = variables ? new Map(sanitizeCanvasVariables(variables).map((v) => [v.key, v.type])) : null;
    const out = {};
    for (const [key, value] of Object.entries(raw)) {
        if (!CANVAS_VARIABLE_KEY_RE.test(key)) continue;
        const type = types ? types.get(key) : (isObj(value) ? "date" : "text");
        if (!type) continue;
        if (type === "date") {
            const date = sanitizeDateValue(value);
            if (date) out[key] = date;
        } else if (typeof value === "string") {
            out[key] = value.slice(0, MAX_TEXT_VALUE);
        }
        if (Object.keys(out).length >= MAX_CANVAS_VARIABLES) break;
    }
    return out;
}

/** `{ key: value }` of every TEXT variable for one block: its own value wins. */
export function textVariablesFor(canvas, block) {
    const own = isObj(block?.config?.variableValues) ? block.config.variableValues : {};
    const out = {};
    for (const v of sanitizeCanvasVariables(canvas?.variables)) {
        if (v.type !== "text") continue;
        out[v.key] = typeof own[v.key] === "string" ? own[v.key] : v.value;
    }
    return out;
}

function dateValueOf(canvas, block, key) {
    const v = sanitizeCanvasVariables(canvas?.variables).find((x) => x.key === key && x.type === "date");
    if (!v) return null;
    const own = isObj(block?.config?.variableValues) ? sanitizeDateValue(block.config.variableValues[key]) : null;
    return own || v.value;
}

/**
 * The date range a block reads with, from the canvas: its "var:<key>" pick, or
 * the canvas-wide date unless the block keeps its own (`dateOverride`).
 * `{ key, value }`, `{ key, value: null }` for a variable that is gone, or null
 * when the block's own fields stand.
 */
export function canvasDateFor(canvas, block) {
    const cfg = block?.config || {};
    if (typeof cfg.dateRange === "string" && cfg.dateRange.startsWith(DATE_VARIABLE_PREFIX)) {
        const key = cfg.dateRange.slice(DATE_VARIABLE_PREFIX.length);
        return { key, value: dateValueOf(canvas, block, key) };
    }
    const key = sanitizeDefaultDateVariable(canvas?.defaultDateVariable, canvas?.variables);
    if (!key || cfg.dateOverride === true || !DATED_BLOCK_TYPES.has(block?.type)) return null;
    return { key, value: dateValueOf(canvas, block, key) };
}

/** `text` with every {key} of `vars` filled in; anything else — {{tokens}} included — is left as it is. */
export function substituteCanvasText(text, vars) {
    if (typeof text !== "string" || !text.includes("{")) return text;
    const map = isObj(vars) ? vars : {};
    return text.replace(TEXT_TOKEN_RE, (whole, key, at, all) => {
        if (all[at - 1] === "{" || all[at + whole.length] === "}") return whole;
        return Object.prototype.hasOwnProperty.call(map, key) ? String(map[key]) : whole;
    });
}

/** `text` with every {{token}} of `scopeVars` (the workspace website, its competitors) filled in. */
export function substituteScopeTokens(text, scopeVars) {
    if (typeof text !== "string" || !text.includes("{{") || !isObj(scopeVars)) return text;
    return text.replace(SCOPE_TOKEN_RE, (whole, expr) => {
        const key = String(expr).trim();
        return Object.prototype.hasOwnProperty.call(scopeVars, key) && scopeVars[key] !== undefined ? String(scopeVars[key]) : whole;
    });
}

/** The {key}s a text uses, in order, deduped. */
export function variablesUsedIn(text) {
    const out = [];
    if (typeof text !== "string") return out;
    text.replace(TEXT_TOKEN_RE, (whole, key, at, all) => {
        if (all[at - 1] !== "{" && all[at + whole.length] !== "}" && !out.includes(key)) out.push(key);
        return whole;
    });
    return out;
}

const SUBSTITUTED_SOURCE_KEYS = ["kwUrl", "kwSeeds"];

function substituteFilters(list, fill) {
    return Array.isArray(list) ? list.map((f) => (isObj(f) && typeof f.val === "string" ? { ...f, val: fill(f.val) } : f)) : list;
}

function substituteReadConfig(cfg, fill) {
    if (!isObj(cfg)) return cfg;
    const out = { ...cfg };
    for (const k of SUBSTITUTED_SOURCE_KEYS) if (typeof out[k] === "string") out[k] = fill(out[k]);
    if (Array.isArray(out.adFilters)) out.adFilters = substituteFilters(out.adFilters, fill);
    if (Array.isArray(out.filters)) out.filters = substituteFilters(out.filters, fill);
    return out;
}

/** Whether any text a run reads holds a {{token}} (the scope's variables are needed). */
export function configUsesScopeTokens(config) {
    return JSON.stringify(config || {}).includes("{{");
}

/**
 * The config a RUN of `block` reads: its prompt, filter values, Keyword Planner
 * settings and text filled in from the canvas variables (and `scopeVars` for
 * {{tokens}}), and its date range taken from the canvas where it should be.
 * Never stored. `dateKey` / `dateMissing` say what happened to the date.
 */
export function resolveBlockConfig(canvas, block, { scopeVars = null } = {}) {
    const cfg = isObj(block?.config) ? block.config : {};
    const vars = textVariablesFor(canvas, block);
    const fill = (s) => substituteScopeTokens(substituteCanvasText(s, vars), scopeVars);
    const out = { ...cfg };
    for (const k of ["prompt", "outputFields", "markdown", "title"]) if (typeof out[k] === "string") out[k] = fill(out[k]);
    if (Array.isArray(out.filters)) out.filters = substituteFilters(out.filters, fill);
    if (isObj(out.sourceFilters)) {
        out.sourceFilters = Object.fromEntries(Object.entries(out.sourceFilters).map(([id, list]) => [id, substituteFilters(list, fill)]));
    }
    if (isObj(out.sourceConfig)) out.sourceConfig = substituteReadConfig(out.sourceConfig, fill);
    if (isObj(out.sourceConfigs)) {
        out.sourceConfigs = Object.fromEntries(Object.entries(out.sourceConfigs).map(([id, c]) => [id, substituteReadConfig(c, fill)]));
    }
    const date = canvasDateFor(canvas, block);
    let dateKey = null;
    let dateMissing = false;
    if (date) {
        for (const f of DATE_VALUE_FIELDS) delete out[f];
        dateKey = date.key;
        if (date.value) Object.assign(out, date.value);
        else { out.dateRange = ""; dateMissing = true; }
    }
    delete out.variableValues;
    delete out.dateOverride;
    return { config: out, dateKey, dateMissing };
}

/** A date value in words: "Last 30 days", "Last 7 days (including today)", "Last 3 months (with this month)", "2026-01-01 → 2026-01-31". */
export function describeDateValue(value) {
    const v = sanitizeDateValue(value) || { dateRange: "" };
    if (v.dateRange === "last_x") return `Last ${v.dateRangeX} days${v.includeToday ? " (including today)" : ""}`;
    if (v.dateRange === "last_x_months") return `Last ${v.dateRangeMonths} months${v.includeCurrentMonth ? " (with this month)" : ""}`;
    if (v.dateRange === "custom") return v.dateFrom || v.dateTo ? `${v.dateFrom || "…"} → ${v.dateTo || "…"}` : "Custom";
    return DATE_RANGE_LABELS[v.dateRange] || v.dateRange;
}

const TEXT_FIELDS_BY_TYPE = { text: ["markdown", "title"], image: ["title", "caption"] };

/**
 * Blocks as a reader sees them: every {key} in a text block (its Markdown and
 * title, and a synthesis copy in its result) and in an image's title and
 * caption filled in from the canvas — the card, the email, the shared link and
 * every export render through this. Other blocks pass through untouched, and
 * so does everything when the canvas has no text variables.
 */
export function withTextVariables(canvas, blocks) {
    const list = Array.isArray(blocks) ? blocks : [];
    if (!sanitizeCanvasVariables(canvas?.variables).some((v) => v.type === "text")) return list;
    return list.map((b) => {
        const fields = TEXT_FIELDS_BY_TYPE[b?.type];
        if (!fields) return b;
        const vars = textVariablesFor(canvas, b);
        const config = { ...(b.config || {}) };
        for (const f of fields) if (typeof config[f] === "string") config[f] = substituteCanvasText(config[f], vars);
        const result = b.result && typeof b.result.markdown === "string"
            ? { ...b.result, markdown: substituteCanvasText(b.result.markdown, vars) }
            : b.result;
        return { ...b, config, result };
    });
}
